import { create, type StoreApi, type StateCreator } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import {
  PurposeOption,
  SelectOption,
} from "../client_onboarding/ClientsGrid/ClientsGrid.utils";

/**
 * Global store interface describing the structure
 * of persisted global lookup/reference data.
 */
interface GlobalStore {
  purposeOptions: PurposeOption[];
  primaryPartnerOptions: SelectOption[];
  officeLocationOptions: SelectOption[];
  users: any[];
  clientTypes: any[];
  clientStatus: any[];
  elStatus: any[];
  elApprovalStatus: any[];
  elSignatureStatus: any[];
  dropdownOptionsCache: Record<string, any[]>;
  version: number;
  hydrated: boolean;

  setGlobalOptions: (data: Partial<GlobalStore>) => void;
  resetGlobalOptions: () => void;
  setHydrated: () => void;
}

const storeInitializer: StateCreator<
  GlobalStore,
  [["zustand/persist", unknown]]
> = (set, get) => ({
  purposeOptions: [],
  primaryPartnerOptions: [],
  officeLocationOptions: [],
  users: [],
  clientTypes: [],
  clientStatus: [],
  elStatus: [],
  elApprovalStatus: [],
  elSignatureStatus: [],
  dropdownOptionsCache: {},

  version: 0,
  hydrated: false,

  setGlobalOptions: (data: Partial<GlobalStore>) =>
    set((state) => ({
      ...state,
      ...data,
      version: state.version + 1,
    })),

  resetGlobalOptions: () =>
    set({
      purposeOptions: [],
      primaryPartnerOptions: [],
      officeLocationOptions: [],
      users: [],
      clientTypes: [],
      clientStatus: [],
      elStatus: [],
      elApprovalStatus: [],
      elSignatureStatus: [],
      dropdownOptionsCache: {},
    }),

  setHydrated: () => set({ hydrated: true }),
});

/**
 * Zustand global store persisted in localStorage.
 * Data persists across page reloads but clears on browser/tab close.
 */
export const useGlobalStore = create<GlobalStore>()(
  persist(storeInitializer, {
    name: "global-options-session-store",
    storage: createJSONStorage(() => sessionStorage),
    onRehydrateStorage: () => (state) => {
      state?.setHydrated();
    },
  })
);